import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetEngagementsQuery } from "../../slices/ExploreCustomers.slice";
import EngagementTable from "./EngagementTable";
import EngagementsAnnotations from "./EngagementsAnnotations";
import EngagementsChart from "./EngagementsChart";

const ProgramsContainer = (props: any) => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const [filteredItems, setFilteredItems] = React.useState([]);
  // Change: chartFilter as array to handle multiple filters
  const [chartFilters, setChartFilters] = React.useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const { data, isLoading, error } = useGetEngagementsQuery(tenantId);

  // Events
  const HandleAPIExcpetion = () => (error && <DisplayAPIsException error={error} height={250} />) || "";

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Hooks
  useEffect(() => {
    logEvent("Programs Tab - Customer Engagements", "Customer Page", "ProgramsContainer", customProps);
  }, []);

  useEffect(() => {
    setFilteredItems(data?.programEngagementDetailsList ?? []);
  }, [data]);

  useEffect(() => {
    const filteredData = (data?.programEngagementDetailsList || []).filter(({ programName, productName }: any) => {
      // If no filters, show all data
      if (chartFilters.length === 0) {
        return true;
      }

      // Apply all filters - item must match ALL active filters
      return chartFilters.every((filter) => {
        if (filter.filterKey === "programName") {
          return programName === filter.value;
        } else if (filter.filterKey === "productName") {
          return productName === filter.value;
        }
        return true;
      });
    });

    setFilteredItems(filteredData);
  }, [chartFilters, data]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  return (
    <>
      <div style={{ display: "flex" }}>
        {(filteredItems?.length && <EngagementsAnnotations annotationData={filteredItems} />) ||
          (isLoading && (
            <div style={{ width: "30%" }}>
              <CustomShimmer columnCount={2} rowCount={9} styles={{ root: { height: 245 } }} />
            </div>
          ))}
        <div style={{ width: "70%" }}>
          <EngagementsChart
            loading={isLoading}
            onSelectedChartFilter={handleChartFilter}
            options={{
              filteredData: filteredItems,
              originalData: data?.programEngagementDetailsList || [],
              chartOptions: [
                {
                  id: 1,
                  field: "programName",
                  key: "customer-view-customer-engagements-programs-chart-by-programName",
                  title: "Program Distribution",
                  type: "donut",
                  kustoLink: data?.programDistributionKustoLink
                },
                {
                  id: 2,
                  field: "productName",
                  key: "customer-view-customer-engagements-programs-chart-by-productName",
                  title: "Product Distribution",
                  type: "donut",
                  kustoLink: data?.productKustoLink
                }
              ]
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <CustomShimmer columnCount={8} />
      ) : (
        (!error && (
          <div style={{ marginLeft: 3, marginRight: 3 }}>
            <EngagementTable
              tableData={filteredItems}
              tenantName={props.tenantName}
              kustoLink={data?.programEngagementDetailsKustoLink}
            />
          </div>
        )) ||
        ""
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export default ProgramsContainer;
